import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ClipboardList, Search, ChevronLeft, ChevronRight, Clock,
  User, Database, FileText, ArrowRight, Filter, X, Printer, FileSpreadsheet,
} from 'lucide-react';
import { exportToCSV, printTable } from '../lib/printUtils';

const ACTION_LABELS = {
  crear: { label: 'Creado', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
  actualizar: { label: 'Modificado', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' },
  eliminar: { label: 'Eliminado', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
};

const ORIGIN_LABELS = {
  web: { label: 'Web', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' },
  sync_access: { label: 'Access', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  sistema: { label: 'Sistema', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
};

const COLLECTION_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'ordenes_compra', label: 'Órdenes de Compra' },
  { value: 'detalle_ordenes_compra', label: 'Detalle OC' },
  { value: 'recepciones', label: 'Recepciones' },
  { value: 'trabajadores', label: 'Trabajadores' },
  { value: 'partes_diarios', label: 'Partes Diarios' },
  { value: 'users', label: 'Usuarios' },
  { value: 'ordenes_trabajo', label: 'Órdenes de Trabajo' },
  { value: 'control_hh', label: 'Control HH' },
];

const PAGE_SIZE = 25;

export default function AuditLogPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({ collection: '', document_id: '', user: '' });
  const [activeFilters, setActiveFilters] = useState({ collection: '', document_id: '', user: '' });
  const [expandedRow, setExpandedRow] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', PAGE_SIZE);
      params.set('skip', page * PAGE_SIZE);
      if (activeFilters.collection) params.set('collection', activeFilters.collection);
      if (activeFilters.document_id) params.set('document_id', activeFilters.document_id);
      if (activeFilters.user) params.set('user', activeFilters.user);

      const res = await api.get(`/admin/audit-log?${params.toString()}`);
      setEntries(res.data.entries || []);
      setTotal(res.data.total || 0);
    } catch (err) { console.error('caught:', err); } finally {
      setLoading(false);
    }
  }, [page, activeFilters]);

  useEffect(() => { loadData(); }, [loadData]);

  const applyFilters = () => {
    setPage(0);
    setActiveFilters({ ...filters });
  };

  const clearFilters = () => {
    setFilters({ collection: '', document_id: '', user: '' });
    setActiveFilters({ collection: '', document_id: '', user: '' });
    setPage(0);
  };

  const hasFilters = activeFilters.collection || activeFilters.document_id || activeFilters.user;

  // Carlos (10/05/2026): imprimir y exportar audit log
  const imprimirAudit = () => {
    if (!entries.length) return;
    printTable({
      title: 'Audit Log',
      subtitle: `${entries.length} entradas (página ${page + 1}) · ${new Date().toLocaleString('es-CL')}`,
      summary: [
        { label: 'Total entradas', value: total },
        { label: 'Página', value: page + 1 },
        { label: 'Colección', value: activeFilters.collection || 'Todas' },
        { label: 'Usuario', value: activeFilters.user || 'Todos' },
      ],
      columns: [
        { key: 'timestamp', label: 'Fecha', format: (v) => v ? new Date(v).toLocaleString('es-CL') : '' },
        { key: 'action', label: 'Acción', align: 'center', format: (v) => ACTION_LABELS[v]?.label || v },
        { key: 'collection', label: 'Tabla' },
        { key: 'document_id', label: 'Doc ID', mono: true },
        { key: 'user_email', label: 'Usuario' },
        { key: 'origin', label: 'Origen', align: 'center', format: (v) => ORIGIN_LABELS[v]?.label || v },
      ],
      rows: entries,
    });
  };

  const exportarAuditExcel = () => {
    if (!entries.length) return;
    exportToCSV({
      filename: `audit_log_pag${page + 1}`,
      title: 'CHIRINO STEEL · Audit Log',
      summary: [
        { label: 'Total entradas (sistema)', value: total },
        { label: 'Entradas en archivo', value: entries.length },
        { label: 'Página', value: page + 1 },
        { label: 'Filtro colección', value: activeFilters.collection || 'Todas' },
        { label: 'Filtro doc ID', value: activeFilters.document_id || '-' },
        { label: 'Filtro usuario', value: activeFilters.user || 'Todos' },
        { label: 'Generado', value: new Date().toLocaleString('es-CL') },
      ],
      columns: [
        { key: 'timestamp', label: 'Timestamp', format: (v) => v ? new Date(v).toLocaleString('es-CL') : '' },
        { key: 'action', label: 'Acción' },
        { key: 'collection', label: 'Tabla' },
        { key: 'document_id', label: 'Doc ID' },
        { key: 'user_email', label: 'Usuario' },
        { key: 'user_role', label: 'Rol' },
        { key: 'origin', label: 'Origen' },
        { key: 'changes_summary', label: 'Resumen cambios', format: (v, r) => {
          if (typeof v === 'string') return v;
          if (r.changes && typeof r.changes === 'object') {
            return Object.keys(r.changes).join(', ');
          }
          return '';
        } },
      ],
      rows: entries,
    });
  };
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const formatTimestamp = (ts) => {
    if (!ts) return '-';
    try {
      const d = new Date(ts);
      return d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    } catch { return ts; }
  };

  const collectionLabel = (col) => {
    const opt = COLLECTION_OPTIONS.find(c => c.value === col);
    return opt ? opt.label : col;
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardList className="h-6 w-6" />
            Registro de Auditoría
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Historial de cambios en el sistema ({total.toLocaleString()} registros)
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card border rounded-xl p-4" data-testid="audit-filters">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Filtros</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Select
            value={filters.collection || "all"}
            onValueChange={(v) => setFilters(f => ({ ...f, collection: v === "all" ? "" : v }))}
          >
            <SelectTrigger data-testid="filter-collection">
              <SelectValue placeholder="Colección" />
            </SelectTrigger>
            <SelectContent>
              {COLLECTION_OPTIONS.map(opt => (
                <SelectItem key={opt.value || "all"} value={opt.value || "all"}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Input
            placeholder="N° Documento (ej: 18504)"
            value={filters.document_id}
            onChange={e => setFilters(f => ({ ...f, document_id: e.target.value }))}
            data-testid="filter-document-id"
          />

          <Input
            placeholder="Usuario (ej: admin2)"
            value={filters.user}
            onChange={e => setFilters(f => ({ ...f, user: e.target.value }))}
            data-testid="filter-user"
          />

          <div className="flex gap-2">
            <Button onClick={applyFilters} className="flex-1" data-testid="apply-filters-btn">
              <Search className="h-4 w-4 mr-1" /> Buscar
            </Button>
            {hasFilters && (
              <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">
                <X className="h-4 w-4" />
              </Button>
            )}
            <Button variant="outline" onClick={imprimirAudit} title="Imprimir página actual" data-testid="btn-print-audit">
              <Printer className="h-4 w-4" />
            </Button>
            <Button variant="outline" onClick={exportarAuditExcel} className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" title="Exportar a Excel" data-testid="btn-excel-audit">
              <FileSpreadsheet className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card border rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p>No se encontraron registros</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-4 py-3 font-medium">Fecha/Hora</th>
                  <th className="text-left px-4 py-3 font-medium">Módulo</th>
                  <th className="text-left px-4 py-3 font-medium">Documento</th>
                  <th className="text-left px-4 py-3 font-medium">Acción</th>
                  <th className="text-left px-4 py-3 font-medium">Origen</th>
                  <th className="text-left px-4 py-3 font-medium">Usuario</th>
                  <th className="text-left px-4 py-3 font-medium">Detalle</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, idx) => {
                  const actionInfo = ACTION_LABELS[entry.action] || { label: entry.action, color: 'bg-gray-100 text-gray-800' };
                  const originInfo = ORIGIN_LABELS[entry.origin] || { label: entry.origin, color: 'bg-gray-100 text-gray-800' };
                  const isExpanded = expandedRow === idx;
                  const hasChanges = entry.changes && entry.changes.length > 0;

                  return (
                    <tr key={idx} className="group" data-testid={`audit-row-${idx}`}>
                      <td className="px-4 py-3 border-b">
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                          <Clock className="h-3 w-3" />
                          {formatTimestamp(entry.timestamp)}
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b">
                        <div className="flex items-center gap-1.5">
                          <Database className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-xs font-medium">{collectionLabel(entry.collection)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b">
                        <div className="flex items-center gap-1.5">
                          <FileText className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="font-mono text-xs font-semibold">{entry.document_id}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${actionInfo.color}`}>
                          {actionInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 border-b">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${originInfo.color}`}>
                          {originInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 border-b">
                        <div className="flex items-center gap-1.5">
                          <User className="h-3.5 w-3.5 text-muted-foreground" />
                          <span className="text-xs">{entry.user}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 border-b">
                        {entry.detail && (
                          <span className="text-xs text-muted-foreground">{entry.detail}</span>
                        )}
                        {hasChanges && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => setExpandedRow(isExpanded ? null : idx)}
                            data-testid={`expand-changes-btn-${idx}`}
                          >
                            {isExpanded ? 'Ocultar' : `${entry.changes.length} cambio${entry.changes.length > 1 ? 's' : ''}`}
                          </Button>
                        )}
                        {isExpanded && hasChanges && (
                          <div className="mt-2 space-y-1">
                            {entry.changes.map((c, ci) => (
                              <div key={ci} className="flex items-center gap-1.5 text-xs bg-muted/50 rounded px-2 py-1">
                                <span className="font-medium text-muted-foreground min-w-[80px]">{c.campo}:</span>
                                <span className="text-red-600 dark:text-red-400 line-through max-w-[150px] truncate">
                                  {c.valor_anterior || '(vacío)'}
                                </span>
                                <ArrowRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                <span className="text-emerald-600 dark:text-emerald-400 max-w-[150px] truncate">
                                  {c.valor_nuevo || '(vacío)'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/30">
            <span className="text-xs text-muted-foreground">
              Mostrando {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} de {total.toLocaleString()}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
                data-testid="prev-page-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium">{page + 1} / {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
                data-testid="next-page-btn"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
